import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PencilIcon, TrashIcon, SparklesIcon } from '@heroicons/react/24/solid';
import { StarIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { PhotoIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import {
  IProjectAsset,
  AssetType,
  CharacterRoleLabels,
  CharacterRoleColors,
  AssetGenerationStatus,
  AssetGenerationStatusLabels,
  AssetGenerationStatusColors,
  getLabelsByLocale,
  Locale,
} from '@packages/global';
import LazyImage from '../common/LazyImage';
import { projectAssetApi } from '../../services/user/projectAsset.api';
import { toast } from 'react-hot-toast';

// 检查是否为认证角色
const isVerifiedCharacter = (asset: IProjectAsset): boolean => {
  if (asset.assetType !== AssetType.CHARACTER) return false;
  if (!asset.assetId) return false;
  if (!asset.conceptImageUrl) return false;

  // 检查 conceptImageUrl 中是否包含 assetId 参数
  try {
    const url = new URL(asset.conceptImageUrl, window.location.origin);
    const assetIdParam = url.searchParams.get('assetId');
    return !!assetIdParam;
  } catch {
    return false;
  }
};

export interface SubjectCardProps {
  subject: IProjectAsset;
  onEdit: () => void;
  onDelete: () => void;
  onPreview: (url: string) => void;
  onGenerate?: (subject: IProjectAsset) => void;
  onUpload?: (subject: IProjectAsset) => void;
}

export default function SubjectCard({ subject, onEdit, onDelete, onPreview, onGenerate, onUpload }: SubjectCardProps) {
  const { t, i18n } = useTranslation(['projectSettings', 'common']);
  const characterRoleLabels = getLabelsByLocale(CharacterRoleLabels, i18n.language as Locale);
  const assetGenerationStatusLabels = getLabelsByLocale(AssetGenerationStatusLabels, i18n.language as Locale);
  const previewUrl = subject.conceptImageUrl || subject.frontViewUrl || null;
  const roleColor = subject.role ? CharacterRoleColors[subject.role] : '#6B7280';
  const roleLabel = subject.role ? characterRoleLabels[subject.role] : null;

  const generationStatus = subject.generationStatus || AssetGenerationStatus.IDLE;
  const isGenerating = generationStatus === AssetGenerationStatus.PENDING ||
    generationStatus === AssetGenerationStatus.GENERATING;
  const statusColor = AssetGenerationStatusColors[generationStatus];

  const isVerified = isVerifiedCharacter(subject);

  const [favorited, setFavorited] = useState(false);
  const [favoriting, setFavoriting] = useState(false);

  const handleFavorite = async () => {
    if (favoriting || favorited) return;
    setFavoriting(true);
    try {
      await projectAssetApi.favoriteAsset(subject.id);
      setFavorited(true);
      toast.success(t('subject.favoritedSuccess'));
    } catch (err: any) {
      toast.error(err.message || t('subject.favoriteFailed'));
    } finally {
      setFavoriting(false);
    }
  };

  return (
    <div className="bg-bg-surface rounded-xl border border-border-subtle p-3 hover:border-border-default transition-colors">
      <div className="flex items-start gap-3 mb-2.5">
        {/* 缩略图 */}
        <div
          className={`flex-shrink-0 w-16 h-16 bg-bg-elevated rounded-lg flex items-center justify-center overflow-hidden relative ${previewUrl ? 'cursor-pointer' : ''}`}
          onClick={() => previewUrl && onPreview(previewUrl)}
        >
          {previewUrl ? (
            <LazyImage
              src={previewUrl}
              alt={subject.name}
              className="w-full h-full object-cover"
              placeholderClassName="w-full h-full"
            />
          ) : (
            <PhotoIcon className="w-6 h-6 text-border-default" />
          )}
          {/* 认证角标 */}
          {isVerified && (
            <div
              className="absolute top-1 right-1 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full p-0.5 shadow-lg ring-1 ring-yellow-300/50"
              title={t('asset.verified')}
            >
              <CheckBadgeIcon className="h-3 w-3 text-white drop-shadow-md" />
            </div>
          )}
          {isGenerating && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>

        {/* 内容 */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
            <h3 className="text-gray-50 font-medium text-sm truncate">{subject.name}</h3>
            {roleLabel && (
              <span
                className="px-1.5 py-0.5 text-[10px] rounded flex-shrink-0"
                style={{ backgroundColor: `${roleColor}20`, color: roleColor }}
              >
                {roleLabel}
              </span>
            )}
            {generationStatus !== AssetGenerationStatus.IDLE && (
              <span
                className="px-1.5 py-0.5 text-[10px] rounded flex-shrink-0"
                style={{ backgroundColor: `${statusColor}20`, color: statusColor }}
              >
                {assetGenerationStatusLabels[generationStatus]}
              </span>
            )}
          </div>
          <p className="text-[12px] text-gray-300 leading-relaxed line-clamp-2">
            {subject.description || t('subject.noDescription')}
          </p>
        </div>
      </div>

      {/* 底部按钮行 */}
      <div className="flex items-center gap-1 border-t border-border-subtle pt-2">
        {isGenerating ? (
          <div className="flex-1 flex items-center justify-center gap-1.5 py-1 text-xs text-gray-400">
            <div className="w-3 h-3 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            {t('common:generating')}
          </div>
        ) : (
          <>
            {onGenerate && (
              <button
                onClick={() => onGenerate(subject)}
                className="flex-1 flex items-center justify-center py-1 text-primary hover:bg-primary/10 rounded-md transition-colors cursor-pointer"
                title={t('subject.aiTextGenerate')}
              >
                <SparklesIcon className="w-3.5 h-3.5" />
              </button>
            )}
            {onUpload && (
              <button
                onClick={() => onUpload(subject)}
                className="flex-1 flex items-center justify-center py-1 text-blue-400 hover:bg-blue-400/10 rounded-md transition-colors cursor-pointer"
                title={t('subject.uploadRefGenerate')}
              >
                <ArrowUpTrayIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </>
        )}
        {/* 收藏 */}
        <button
          onClick={handleFavorite}
          disabled={favoriting || favorited}
          className="flex-1 flex items-center justify-center py-1 text-gray-400 hover:text-yellow-400 hover:bg-yellow-400/10 rounded-md transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          title={favorited ? t('subject.favorited') : t('subject.favoriteToLibrary')}
        >
          {favorited ? (
            <StarIconSolid className="w-3.5 h-3.5 text-yellow-400" />
          ) : (
            <StarIcon className="w-3.5 h-3.5" />
          )}
        </button>
        <button
          onClick={onEdit}
          className="flex-1 flex items-center justify-center py-1 text-gray-400 hover:text-gray-50 hover:bg-bg-elevated rounded-md transition-colors cursor-pointer"
          title={t('common:edit')}
        >
          <PencilIcon className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={onDelete}
          className="flex-1 flex items-center justify-center py-1 text-gray-400 hover:text-red-400 hover:bg-red-400/10 rounded-md transition-colors cursor-pointer"
          title={t('common:delete')}
        >
          <TrashIcon className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
